import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { Box, Button, Group, Paper, Stack } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import { IconPlus } from "@tabler/icons-react";
import { isEmpty } from "lodash";

import MissingPaymentMethodBanner from "@/billing/components/MissingPaymentMethodBanner";
import { useBillingPipelineFeature } from "@/billing/hooks/useBillingPipelineFeature";
import { TextWrapper } from "@/components/TextWrapper";
import { EncounterContext } from "@/contexts/encounter";
import { PatientContext } from "@/contexts/patient";
import {
  BillingSectionSaveProvider,
  useBillingSectionSaveCoordinator,
} from "@/encounters/contexts/BillingSectionSaveContext";
import useSaveBillingInfo from "@/encounters/hooks/useSaveBillingInfo";
import useSendEncounterServices from "@/encounters/hooks/useSendEncounterServices";
import { Status } from "@/encounters/zod-schemas/encounters";
import { DevelopmentFeatureFlags, FeatureGate, PracticeFeatureFlags } from "@/feature-flags";
import PermissionGate from "@/permission/components/PermissionGate";
import { BILLER_MANAGER_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import {
  useClientBillingPaymentMethod,
  useMissingPaymentMethodBanner,
} from "@/shared/client-billing-payment-method/hooks";
import { CptCopyrightNotice } from "@/shared/cpt-copyright";
import { selectAllProcedures } from "@/store/slices/procedures";
import ProceduresForm, {
  ProceduresFormType,
  ProceduresFormTypeWithId,
} from "@/zod-schemas/procedures";

import { BillerSection } from "../biller-section";
import EncounterProceduresNoData, {
  EncounterProceduresNoDataVariant,
} from "../EncounterProcedures/components/EncounterProceduresNoData";
import { toProceduresFormValues } from "../EncounterProcedures/utils/procedure";
import { EncounterTimeSpent } from "../EncounterTimeSpent";

import EncounterBillingDrawer from "./components/EncounterBillingDrawer";
import EncounterBillingHeader from "./components/EncounterBillingHeader";
import EncounterBillingTable from "./components/EncounterBillingTable";
import {
  canSaveEncounterBillingInfo,
  canSendEncounterBillingToServices,
  isEncounterSignedAndLockedForBilling,
} from "./utils/encounter-billing-action-gates";

const BILLING_ACTIONS_HELPER_TEXT =
  "Save billing info or send billing info to the billing services";

const EncounterBilling = () => {
  const [opened, handlers] = useDisclosure(false);
  const location = useLocation();
  const { encounter } = useContext(EncounterContext);
  const { patient } = useContext(PatientContext);
  const procedures = useSelector(selectAllProcedures);
  const [targetCptCode, setTargetCptCode] = useState<ProceduresFormTypeWithId | undefined>(
    undefined,
  );
  const isBillingPipelineEnabled = useBillingPipelineFeature();
  const billingSectionSave = useBillingSectionSaveCoordinator();
  const { isSending, sendServices } = useSendEncounterServices(encounter?.id);
  const { isSaving, saveBillingInfo } = useSaveBillingInfo(billingSectionSave);
  const { paymentMethodDisplay, insuranceDisplay, isPaymentMethodUndetermined, isLoading } =
    useClientBillingPaymentMethod(isBillingPipelineEnabled ? patient?.id : undefined);

  const isAmendment = location.pathname.endsWith("amend");
  const isEncounterActive =
    encounter?.status !== Status.Finished && encounter?.status !== Status.Cancelled;
  const isCptCodeDataEmpty = isEmpty(procedures);
  const { hasProviderOrScribeRole, hasBillerRole } = usePractitionerPermissions();
  const canAddOrEditProcedures = hasProviderOrScribeRole || hasBillerRole;
  // PE-3386 AC2: Save/Send stay unavailable until the encounter is signed and locked (finished).
  const canSaveBillingInfo = canSaveEncounterBillingInfo(encounter?.status);
  const canSendToServices = canSendEncounterBillingToServices({
    encounterStatus: encounter?.status,
    isPaymentMethodLoading: isLoading,
    isPaymentMethodUndetermined,
  });
  const {
    showBanner: isMissingPrimaryPaymentMethodForSendToServices,
    dismissBanner: dismissMissingPaymentMethodBanner,
  } = useMissingPaymentMethodBanner({
    isEligible:
      isBillingPipelineEnabled &&
      Boolean(patient?.id) &&
      isEncounterSignedAndLockedForBilling(encounter?.status),
    isLoading,
    isPaymentMethodUndetermined,
    scopeId: encounter?.id,
  });

  const handleTargetCptCode = (cptCode: ProceduresFormTypeWithId | undefined) => {
    setTargetCptCode(cptCode);
  };

  // Saves whatever the section below is holding.
  const handleSaveBillingInfo = async () => {
    if (!canSaveBillingInfo) return;

    await saveBillingInfo();
  };

  // Sends every billing code the encounter has captured, which is what marks it done with billing.
  //
  // The section below is saved first, because what it holds only reaches the server on Save Billing
  // Info. Sending without that would mark the encounter fully sent while a code the biller had just
  // added had never been captured, let alone billed, and the encounter would leave the Missing
  // Services queue with that code missing. A save that fails stops the send for the same reason: the
  // biller still has work in front of them, and it belongs in what goes.
  //
  // The save is passed to the send rather than awaited before it, so `isSending` covers both. A guard
  // taken only once the save was done left the button live while it ran, and two quick clicks could
  // each start a save and then a send of their own.
  const handleSendToServices = async () => {
    if (!canSendToServices) return;

    await sendServices({
      beforeSend: async () => (await billingSectionSave.saveAll()) !== "failed",
    });
  };

  const form = useForm<ProceduresFormType>({
    validate: zodResolver(ProceduresForm),
  });

  useEffect(() => {
    if (targetCptCode) {
      form.setValues(toProceduresFormValues(targetCptCode));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCptCode]);

  // Wrapper function to open drawer (EncounterProceduresNoData expects a function that takes a drawer name)
  const openDrawer = (_drawerName: string) => {
    handlers.open();
  };

  if (!encounter || !patient) return null;

  // Two sources for the same tab. The billing pipeline reads the ChargeItems the encounter captured
  // when it was signed and locked; without it the tab reads the CPT-code questionnaire responses it
  // has always read. Both stay live while the pipeline rolls out.
  //
  // Only the body swaps: the Save / Send actions below are the same either way, so they sit outside
  // and neither implementation can drift from the other on what a biller can do here.
  const proceduresBody = (
    <>
      <EncounterBillingDrawer
        form={form}
        opened={opened}
        onClose={handlers.close}
        targetCptCode={targetCptCode}
        handleTargetCptCode={handleTargetCptCode}
      />
      <EncounterBillingHeader
        encounter={encounter}
        insuranceDisplay={insuranceDisplay}
        locationDisplay={encounter?.class?.display}
        paymentMethodDisplay={paymentMethodDisplay}
      />
      {isCptCodeDataEmpty ? (
        <EncounterProceduresNoData
          mt="xl"
          allowBillerRole
          open={openDrawer}
          title="Procedure Codes"
          isAmendment={isAmendment}
          drawerName="billing-drawer"
          isEncounterActive={isEncounterActive}
          variant={EncounterProceduresNoDataVariant.noBorder}
        />
      ) : (
        <Stack mt="xl" gap="md">
          <Group justify="space-between">
            <TextWrapper fw="bold" c="dark.9">
              Procedure Codes
            </TextWrapper>
            {(isEncounterActive || isAmendment) && canAddOrEditProcedures && (
              <Button
                size="sm"
                variant="outline"
                color="burgundy.9"
                onClick={handlers.open}
                leftSection={<IconPlus size={16} />}
                data-testid="billing-add-procedure-button"
              >
                Add More
              </Button>
            )}
          </Group>
          <CptCopyrightNotice size="sm" />
          <EncounterBillingTable
            open={handlers.open}
            procedures={procedures}
            handleTargetCptCode={handleTargetCptCode}
          />
        </Stack>
      )}
    </>
  );

  return (
    <>
      <Box mt="lg">
        <EncounterTimeSpent />
      </Box>
      <Paper p="xl" mt="lg">
        {/* Whichever body is live, it is what Save Billing Info below saves. */}
        <BillingSectionSaveProvider coordinator={billingSectionSave}>
          {isBillingPipelineEnabled ? (
            // Adding codes is offered under the same condition as Save Billing Info, so the section
            // never stages work the tab has no way to write.
            <BillerSection
              encounter={encounter}
              insuranceDisplay={insuranceDisplay}
              canAddProcedures={canSaveBillingInfo}
              paymentMethodDisplay={paymentMethodDisplay}
            />
          ) : (
            proceduresBody
          )}
        </BillingSectionSaveProvider>
        <FeatureGate name={PracticeFeatureFlags.Billing}>
          <FeatureGate name={DevelopmentFeatureFlags.PracticeBilling}>
            <PermissionGate requiredPermissions={BILLER_MANAGER_PERMISSION_FLAGS}>
              <Stack mt="xl" gap="sm">
                {isMissingPrimaryPaymentMethodForSendToServices && (
                  <MissingPaymentMethodBanner onDismiss={dismissMissingPaymentMethodBanner} />
                )}
                <Group gap="sm" align="center" justify="flex-end">
                  <TextWrapper maw={390} c="gray.7">
                    {BILLING_ACTIONS_HELPER_TEXT}
                  </TextWrapper>
                  {/* Each button is disabled while the other action runs. See useSaveBillingInfo for why
                      an overlap would leave the encounter in the wrong place on the Missing Services queue. */}
                  <Button
                    variant="outline"
                    color="burgundy.9"
                    loading={isSaving}
                    onClick={handleSaveBillingInfo}
                    data-testid="save-billing-info-button"
                    disabled={!canSaveBillingInfo || isSending}
                  >
                    Save Billing Info
                  </Button>
                  <Button
                    variant="filled"
                    color="burgundy.9"
                    loading={isSending}
                    onClick={handleSendToServices}
                    data-testid="send-to-services-button"
                    disabled={!canSendToServices || isSending || isSaving}
                  >
                    Send to Services
                  </Button>
                </Group>
              </Stack>
            </PermissionGate>
          </FeatureGate>
        </FeatureGate>
      </Paper>
    </>
  );
};

export default EncounterBilling;
